import React, { useState, useRef, useEffect } from 'react';
import './EditableDropdown.css';

const EditableDropdown = ({
  label,
  value,
  options = [],
  onChange,
  onCreateNew,
  onStartCreating, // New prop to handle when user starts creating
  disabled = false,
  placeholder = "Select...",
  addNewText = "Add New...",
  maxLength,
  forceInput = false, // New prop to force input mode
  showAllOption = false, // New prop to show "All" option
  allOptionValue = "All", // New prop for the "All" option text
  temporaryItems = [] // New prop for temporary items to show after "Add New"
}) => {
  const [isEditing, setIsEditing] = useState(forceInput);
  const [newItemName, setNewItemName] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if ((isEditing || forceInput) && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing, forceInput]);

  useEffect(() => {
    setIsEditing(forceInput);
    if (forceInput) {
      setNewItemName(''); // Clear the input when forcing to input mode
    }
  }, [forceInput]);

  const handleSelectChange = (e) => {
    const selectedValue = e.target.value;
    
    if (selectedValue === '__ADD_NEW__') {
      // Reset the select back to its current value to prevent visual change
      e.target.value = value;
      // Immediately call onCreateNew to open the dialog
      if (onCreateNew) {
        onCreateNew();
      }
    } else {
      // This handles both empty string (blank selection) and actual values
      onChange(selectedValue);
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateNew();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleCreateNew = async () => {
    const trimmedName = newItemName.trim();
    if (!trimmedName) {
      handleCancelEdit();
      return;
    }

    try {
      await onCreateNew(trimmedName);
      onChange(trimmedName);
      // Only set isEditing to false if not forceInput
      // forceInput should be controlled by parent component
      if (!forceInput) {
        setIsEditing(false);
      }
      setNewItemName('');
    } catch (error) {
      console.error('Failed to create new item:', error);
      // Keep editing mode open on error
    }
  };

  const handleCancelEdit = () => {
    // Only exit editing mode if not forceInput
    if (!forceInput) {
      setIsEditing(false);
    }
    setNewItemName('');
  };

  const handleInputBlur = () => {
    // Small delay to allow for button clicks
    setTimeout(() => {
      if (newItemName.trim()) {
        handleCreateNew();
      } else {
        handleCancelEdit();
      }
    }, 150);
  };

  if (isEditing || forceInput) {
    return (
      <div className="dropdown-group">
        <label>{label}:</label>
        <div className="editable-dropdown editing">
          <input
            ref={inputRef}
            type="text"
            value={newItemName}
            onChange={(e) => setNewItemName(e.target.value)}
            onKeyDown={handleInputKeyDown}
            onBlur={handleInputBlur}
            placeholder={`Enter new ${label.toLowerCase()}...`}
            maxLength={maxLength}
            className="dropdown-input"
          />
          <div className="edit-buttons">
            <button 
              type="button"
              onClick={handleCreateNew}
              className="confirm-btn"
              disabled={!newItemName.trim()}
            >
              ✓
            </button>
            <button 
              type="button"
              onClick={handleCancelEdit}
              className="cancel-btn"
            >
              ✕
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dropdown-group">
      <label>{label}:</label>
      <select 
        value={value} 
        onChange={handleSelectChange} 
        disabled={disabled}
        className="editable-dropdown-select"
      >
        <option value="" disabled={value !== ""}> </option>
        <option value="__ADD_NEW__" className="add-new-option">
          {addNewText}
        </option>
        {showAllOption && (
          <>
            <option disabled className="dropdown-spacer">─────────</option>
            <option value={allOptionValue}>{allOptionValue}</option>
          </>
        )}
        {options.length > 0 && (
          <>
            <option disabled className="dropdown-spacer">─────────</option>
            {options.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </>
        )}
        {temporaryItems.length > 0 && (
          <>
            <option disabled className="dropdown-spacer">─────────</option>
            {temporaryItems.map((item, index) => (
              <option key={`temp-${index}`} value={item} className="temporary-option">
                {item}
              </option>
            ))}
          </>
        )}
      </select>
    </div>
  );
};

export default EditableDropdown;